<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue"
import { useTranslation } from "@/composables"

interface ReadMoreProps {
  as?: string
  content?: string
  moreText?: string
  lessText?: string
  lines?: number
}

const props = withDefaults(defineProps<ReadMoreProps>(), {
  as: "p",
  content: "",
  lines: 3,
})

const { t } = useTranslation()

const contentRef = ref<HTMLElement | null>(null)
const expanded = ref<boolean>(false)
const canExpand = ref<boolean>(true)
const isCalculating = ref<boolean>(false)
const resizeObserver = ref<ResizeObserver | null>(null)

function toggle() {
  expanded.value = !expanded.value
}

function measureContent() {
  if (isCalculating.value || !contentRef.value)
    return

  isCalculating.value = true

  // Account for sub-pixel rounding differences between browsers
  const overflowTolerance = 1

  requestAnimationFrame(() => {
    nextTick(() => {
      if (contentRef.value) {
        const element = contentRef.value
        const clone = element.cloneNode(true) as HTMLElement
        const parent = element.parentElement
        if (!parent) {
          isCalculating.value = false
          return
        }
        const computedStyle = window.getComputedStyle(element)

        const fontSize = Number.parseFloat(computedStyle.fontSize || "0")
        const lineHeightValue = computedStyle.lineHeight
        const lineHeight = Number.isNaN(Number.parseFloat(lineHeightValue))
          ? fontSize * 1.2
          : Number.parseFloat(lineHeightValue)
        const clampHeight = lineHeight * (props.lines ?? 3)

        const elementWidth = element.getBoundingClientRect().width
        const parentWidth = parent?.getBoundingClientRect().width ?? 0
        const width = elementWidth || parentWidth

        clone.classList.remove("gpx-read-more_clamped")
        clone.style.position = "absolute"
        clone.style.visibility = "hidden"
        clone.style.pointerEvents = "none"
        clone.style.height = "auto"
        clone.style.maxHeight = "none"
        clone.style.width = width ? `${width}px` : "auto"
        clone.style.boxSizing = "border-box"
        clone.style.display = "block"

        parent?.appendChild(clone)
        const shouldExpand
          = clone.scrollHeight - clampHeight > overflowTolerance
        clone.remove()

        canExpand.value = Boolean(shouldExpand)
      }
      isCalculating.value = false
    })
  })
}

onMounted(() => {
  measureContent()

  if (contentRef.value) {
    resizeObserver.value = new ResizeObserver(() => measureContent())
    resizeObserver.value.observe(contentRef.value)
  }

  if (document?.fonts?.ready) {
    document.fonts.ready.then(() => measureContent())
  }
})

onBeforeUnmount(() => {
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
  }
  resizeObserver.value = null
})

watch(
  () => props.content,
  () => {
    // Reset expansion state when content changes
    expanded.value = false
    measureContent()
  },
)

const moreLabel = computed(() => {
  return props.moreText ?? t("read_more.more_label")
})

const lessLabel = computed(() => {
  return props.lessText ?? t("read_more.less_label")
})
</script>

<template>
  <component
    v-bind="$attrs"
    :is="as"
    ref="contentRef"
    class="gpx-read-more"
    :class="{ 'gpx-read-more_clamped': !expanded }"
  >
    {{ content }}
  </component>
  <slot v-bind="{ toggle, expanded, canExpand }">
    <button v-if="canExpand" class="gpx-read-more-button" @click="toggle">
      {{ expanded ? lessLabel : moreLabel }}
    </button>
  </slot>
</template>

<style scoped>
.gpx-read-more {
  white-space: pre-wrap;
  margin: 0;
  padding: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.gpx-read-more_clamped {
  display: -webkit-box;
  -webkit-line-clamp: v-bind(lines);
  line-clamp: v-bind(lines);
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.gpx-read-more-button {
  background: none;
  margin: 0.75rem 0 0;
  padding: 0;
  border: none;
  cursor: pointer;
  transition: color 150ms linear;
  text-decoration: underline;
  font-size: 0.9rem;
}

.gpx-read-more-button:hover,
.gpx-read-more-button:focus {
  color: var(--template-primary-colour);
}
</style>
